import {
  ChatActionOutcome,
  ChatCompletion,
  ChatCompletionRequest,
  ChatKeyUpdate,
  ChatProviderModel,
  ChatReindexResult,
  ChatSession,
  ChatSessionCreateRequest,
  ChatSessionRenameRequest,
  ChatSessionSummary,
  ChatSettings,
  ChatSettingsUpdate,
  ChatStatus,
  ChatSuggestion,
  Response,
} from "@core/model";
import {ApiBuilder} from "..";

/**
 * Contratto del proxy chatbot (predimain-backend, branch chatbot).
 * Stessi header delle altre API (baseQuery): Authorization Bearer e,
 * solo se il ruolo è ADMIN, X-Sw-Tenant-Id. Le chiavi dei provider
 * restano sul backend e non compaiono qui.
 *
 * Envelope ResponseDto: { data, message, code }.
 * Successo: code 0, message vuoto. Il payload è in data.
 *
 * GET /api/chatbot/providers
 *   data: { id, label }[]
 *   Solo i provider che hanno una chiave. Non c'è models.
 *   Il pannello non lo chiama.
 * GET /api/chatbot/providers/{provider}/models
 *   data: { id, name? }[]
 *   Solo la pagina impostazioni. Senza chiave: la pagina invita a salvarla.
 *   Si può salvare solo un id di questa lista. Se il modello salvato
 *   non c'è, il select resta vuoto: non si sceglie un altro al suo posto.
 *
 * POST /api/chatbot/messages
 *   body: { messages: { role, content }[], timeZone?, sessionId? }
 *   timeZone è il fuso del browser (es. Europe/Rome), per date e orari relativi.
 *   sessionId lega la conversazione a una sessione salvata dell'utente; senza id
 *   la chat resta solo in memoria sul client. Dopo una risposta riuscita user e
 *   assistant vengono scritti in sessione (al più 50 messaggi).
 *   Provider e modello sono quelli del tenant, non del body.
 *   maxTokens è opzionale e non viene inviato.
 *   Il ruolo system è rifiutato: i messaggi system non partono.
 *   data: { role: "assistant", content, action? }
 *   action c'è solo se il modello propone una scrittura: { id, label, summary, details, arguments? }.
 *   details: una riga per campo ("Asset: Pressa P3"). Una nuova proposta sostituisce la precedente.
 *   arguments, se c'è, è l'oggetto che verrà eseguito. La scheda mostra ogni campo
 *   (anche la priorità) come testo, senza interpretare l'HTML del modello.
 *   choices c'è solo se il modello chiede un dato con risposte pronte: { question, options, multiple }.
 *
 * POST /api/chatbot/messages/stream
 *   stesso body, Accept: text/event-stream
 *   eventi: data:{"delta":"..."} senza spazio dopo data:
 *   data:{"action":{ id, label, summary, details }} dopo il testo, prima di [DONE],
 *   se il modello propone una scrittura (al più una per risposta).
 *   data:{"choices":{ question, options, multiple }} se invece chiede un dato: le opzioni
 *   diventano pulsanti e il clic le invia come messaggio dell'utente. Con multiple
 *   si scelgono più opzioni e Invia le manda unite da ", ".
 *   chiusura: data:[DONE]
 *   nessuna riga event:
 *   Errori di validazione, ruolo o provider: HTTP + ResponseDto prima dello stream.
 *   Se il body ha message, il pannello mostra quello.
 *   Errore a stream già aperto: data con code e message, oppure ancora
 *   un delta di testo seguito da data:[DONE].
 *   Senza un testo leggibile si usa error.500.
 *   404 o 405: il client ripiega sulla POST non in streaming.
 *
 * 503 provider senza chiave, 502 chiave rifiutata (non è un JWT scaduto),
 * 429 rate limit. 1505: connessione al provider interrotta, dopo un retry.
 * Se c'è message, il pannello mostra quello.
 * Lo streaming sta in components/chatbot/stream.ts.
 *
 * Impostazioni (stesso envelope, payload in data):
 * GET /api/chatbot/status
 *   data: { enabled, configured, provider, model }
 *   Anche da spento.
 *   Ruoli: ADMIN, TENANT_ADMIN, OPERATOR. MACHINE non lo chiama.
 *   Senza tenant: HTTP 400, code 1507.
 * GET /api/chatbot/settings
 *   data: { enabled, provider, model, keys }
 *   Da spento: HTTP 403, code 1508.
 * PUT /api/chatbot/settings
 *   body: { provider, model }
 *   L'accensione sta sul tenant, non qui:
 *   PUT o PATCH /api/admin/tenant/{id} con chatbot: { enabled }.
 * PUT /api/chatbot/settings/keys/{provider}
 *   body: { key }
 * DELETE /api/chatbot/settings/keys/{provider}
 *   keys è un array: { provider, hasKey, last4 }[]
 *   (openai, anthropic, xai, ...)
 *   La pagina mostra solo la chiave del provider selezionato.
 *   Chiave presente: campo mascherato in sola lettura e solo Modifica.
 *   Dopo Modifica: campo vuoto, Salva chiave, Rimuovi e Annulla.
 *   Annulla scarta la bozza. Dopo salvataggio o rimozione si torna
 *   allo stato bloccato. Senza chiave: campo editabile e solo Salva chiave.
 *   Il cambio di tenant o di provider chiude la modifica.
 * ADMIN usa il chatbot del tenant selezionato (X-Sw-Tenant-Id).
 * Status, impostazioni e modelli usano l'id del tenant come chiave
 * di cache. L'id non entra nell'URL: l'header resta in baseQuery.
 * Il cambio tenant invalida il tag chatbot.
 * FAB e impostazioni nella pagina Amministrazione seguono status.enabled
 * del tenant selezionato, anche per ADMIN. Senza tenant non parte
 * la GET e niente FAB.
 * Chat, impostazioni e reindex da spento: HTTP 403, code 1508,
 * message "Il chatbot è disattivato per questo tenant".
 * Il messaggio resta sulla pagina, senza snackbar globale.
 *
 * Azioni proposte dal chatbot (ticket e attività), stesso envelope:
 * POST /api/chatbot/actions/{id}/confirm
 *   body vuoto. Nell'URL c'è solo l'id, codificato: gli argomenti non tornano indietro.
 *   Esegue l'azione con la sessione di chi conferma.
 *   data: { done, message }. done false: la piattaforma l'ha rifiutata.
 *   404 code 1512: azione scaduta, già eseguita, annullata o sostituita.
 * DELETE /api/chatbot/actions/{id}
 *   204. Scarta l'azione senza eseguirla.
 * La conferma invalida ticket, task, artifact e audit, così le pagine aperte
 * (anche I miei task e il gantt) si aggiornano.
 * GET /api/chatbot/suggestions
 *   data: [{ kind, count }], kind CHECKLIST (task con step senza risposta)
 *   o MY_TICKETS (ticket da fare o in lavorazione assegnati all'utente).
 *   Solo quelli con count > 0, calcolati senza chiamare il modello.
 *   Pulsanti del pannello vuoto: si rileggono dopo ogni conferma.
 *   Errore, body vuoto o forma inattesa: nessun pulsante e nessuna snackbar.
 *
 * Sessioni salvate (stesso envelope, stessi ruoli e vincoli della chat):
 * GET /api/chatbot/sessions
 *   data: [{ id, title, createdAt, updatedAt }], ordine per updatedAt decrescente.
 * POST /api/chatbot/sessions
 *   body: { title? }. Titolo facoltativo, altrimenti «Nuova chat».
 *   data: { id, title, createdAt, updatedAt }.
 * GET /api/chatbot/sessions/{id}
 *   data: { id, title, createdAt, updatedAt, messages: [{ role, content }] }.
 *   Solo il proprietario; altrimenti 404.
 * PATCH /api/chatbot/sessions/{id}
 *   body: { title }. Rinomina; data come POST.
 * DELETE /api/chatbot/sessions/{id}
 *   204. Cancella sessione e messaggi.
 *
 * Documenti già caricati in PrediMain. Non esiste più GET /api/chatbot/knowledge.
 * POST /api/chatbot/knowledge/reindex
 *   body vuoto
 *   data: { started, indexed, skipped, failed }
 *   503 code 1510: servizio di embedding non configurato.
 * Se il backend cambia forma, si adatta solo `payload`.
 */

const PROVIDERS_URL = "/api/chatbot/providers";
const MESSAGES_URL = "/api/chatbot/messages";
const STATUS_URL = "/api/chatbot/status";
const SETTINGS_URL = "/api/chatbot/settings";
const REINDEX_URL = "/api/chatbot/knowledge/reindex";
const ACTIONS_URL = "/api/chatbot/actions";
const SUGGESTIONS_URL = "/api/chatbot/suggestions";
const SESSIONS_URL = "/api/chatbot/sessions";

const CHAT_STREAM_URL = "/api/chatbot/messages/stream";

// Nomi dei provider nella pagina impostazioni. I modelli arrivano da
// GET /api/chatbot/providers/{provider}/models.
const CHAT_SETTINGS_PROVIDERS: {id: string; label: string}[] = [
  {id: "openai", label: "OpenAI"},
  {id: "anthropic", label: "Anthropic"},
  {id: "xai", label: "xAI"},
];

const payload = <T>(response: Response<T>) => response.data;

const fetchChatProviderModels = (builder: ApiBuilder) =>
  builder.query<ChatProviderModel[], {tenantId: string; provider: string}>({
    query: ({provider}) => ({
      url: `${PROVIDERS_URL}/${provider}/models`,
      method: "GET",
    }),
    providesTags: ["chatbot"],
    transformResponse: (response: Response<ChatProviderModel[]>) =>
      payload(response),
  });

const sendChatMessage = (builder: ApiBuilder) =>
  builder.mutation<ChatCompletion, ChatCompletionRequest>({
    query: body => ({
      url: MESSAGES_URL,
      method: "POST",
      body,
    }),
    transformResponse: (response: Response<ChatCompletion>) =>
      payload(response),
  });

const fetchChatStatus = (builder: ApiBuilder) =>
  builder.query<ChatStatus, string>({
    query: () => ({
      url: STATUS_URL,
      method: "GET",
    }),
    providesTags: ["chatbot"],
    transformResponse: (response: Response<ChatStatus>) => payload(response),
  });

const fetchChatSuggestions = (builder: ApiBuilder) =>
  builder.query<ChatSuggestion[], string>({
    query: () => ({
      url: SUGGESTIONS_URL,
      method: "GET",
    }),
    providesTags: ["ticket", "artifact"],
    transformResponse: (response: Response<ChatSuggestion[]>) =>
      payload(response),
  });

const fetchChatSettings = (builder: ApiBuilder) =>
  builder.query<ChatSettings, string>({
    query: () => ({
      url: SETTINGS_URL,
      method: "GET",
    }),
    providesTags: ["chatbot"],
    transformResponse: (response: Response<ChatSettings>) => payload(response),
  });

const updateChatSettings = (builder: ApiBuilder) =>
  builder.mutation<ChatSettings, ChatSettingsUpdate>({
    query: body => ({
      url: SETTINGS_URL,
      method: "PUT",
      body,
    }),
    invalidatesTags: ["chatbot"],
    transformResponse: (response: Response<ChatSettings>) => payload(response),
  });

const saveChatKey = (builder: ApiBuilder) =>
  builder.mutation<void, ChatKeyUpdate>({
    query: ({provider, key}) => ({
      url: `${SETTINGS_URL}/keys/${provider}`,
      method: "PUT",
      body: {key},
    }),
    invalidatesTags: ["chatbot"],
  });

const deleteChatKey = (builder: ApiBuilder) =>
  builder.mutation<void, string>({
    query: provider => ({
      url: `${SETTINGS_URL}/keys/${provider}`,
      method: "DELETE",
    }),
    invalidatesTags: ["chatbot"],
  });

const reindexChatKnowledge = (builder: ApiBuilder) =>
  builder.mutation<ChatReindexResult, void>({
    query: () => ({
      url: REINDEX_URL,
      method: "POST",
      body: {},
    }),
    transformResponse: (response: Response<ChatReindexResult>) =>
      payload(response),
  });

const confirmChatAction = (builder: ApiBuilder) =>
  builder.mutation<ChatActionOutcome, string>({
    query: id => ({
      url: `${ACTIONS_URL}/${encodeURIComponent(id)}/confirm`,
      method: "POST",
      body: {},
    }),
    invalidatesTags: ["ticket", "task", "artifact", "audit"],
    transformResponse: (response: Response<ChatActionOutcome>) =>
      payload(response),
  });

const cancelChatAction = (builder: ApiBuilder) =>
  builder.mutation<void, string>({
    query: id => ({
      url: `${ACTIONS_URL}/${encodeURIComponent(id)}`,
      method: "DELETE",
    }),
  });

const fetchChatSessions = (builder: ApiBuilder) =>
  builder.query<ChatSessionSummary[], string>({
    query: () => ({
      url: SESSIONS_URL,
      method: "GET",
    }),
    providesTags: ["chatbot"],
    transformResponse: (response: Response<ChatSessionSummary[]>) =>
      payload(response),
  });

const fetchChatSession = (builder: ApiBuilder) =>
  builder.query<ChatSession, string>({
    query: id => ({
      url: `${SESSIONS_URL}/${encodeURIComponent(id)}`,
      method: "GET",
    }),
    providesTags: ["chatbot"],
    transformResponse: (response: Response<ChatSession>) => payload(response),
  });

const createChatSession = (builder: ApiBuilder) =>
  builder.mutation<ChatSessionSummary, ChatSessionCreateRequest | void>({
    query: body => ({
      url: SESSIONS_URL,
      method: "POST",
      body: body ?? {},
    }),
    invalidatesTags: ["chatbot"],
    transformResponse: (response: Response<ChatSessionSummary>) =>
      payload(response),
  });

const renameChatSession = (builder: ApiBuilder) =>
  builder.mutation<
    ChatSessionSummary,
    {id: string; body: ChatSessionRenameRequest}
  >({
    query: ({id, body}) => ({
      url: `${SESSIONS_URL}/${encodeURIComponent(id)}`,
      method: "PATCH",
      body,
    }),
    invalidatesTags: ["chatbot"],
    transformResponse: (response: Response<ChatSessionSummary>) =>
      payload(response),
  });

const deleteChatSession = (builder: ApiBuilder) =>
  builder.mutation<void, string>({
    query: id => ({
      url: `${SESSIONS_URL}/${encodeURIComponent(id)}`,
      method: "DELETE",
    }),
    invalidatesTags: ["chatbot"],
  });

export {
  fetchChatProviderModels,
  sendChatMessage,
  fetchChatStatus,
  fetchChatSuggestions,
  fetchChatSettings,
  updateChatSettings,
  saveChatKey,
  deleteChatKey,
  reindexChatKnowledge,
  confirmChatAction,
  cancelChatAction,
  fetchChatSessions,
  fetchChatSession,
  createChatSession,
  renameChatSession,
  deleteChatSession,
  CHAT_STREAM_URL,
  CHAT_SETTINGS_PROVIDERS,
};
